import {
  DashboardLayout,
  BlockRow,
  LayoutColumn,
  WebDashboardContentSpecBlocksInner,
} from "@coeff/agent-types";

import { TableContentHeightReport } from "./dataTableSizing";

// --------------------------------
// Types
// --------------------------------

type Block = WebDashboardContentSpecBlocksInner;

// --------------------------------
// Shared constants
// --------------------------------

export const COL_SPAN_CLASS_MAP: Record<number, string> = {
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
};

// Maximum number of columns allowed per row (matches 12-column grid)
export const MAX_COLUMNS_PER_ROW = 12;

// Minimum row height (enforced during resize and layout repair)
export const MIN_ROW_HEIGHT = 150;

// A table-only row whose content just misses fitting grows to its content instead
// of slicing its last row at the fold; anything needing more than this keeps the
// stored height and scrolls. ~1.5 data rows at the default table row height.
export const TABLE_GROW_TO_FIT_TOLERANCE_PX = 60;

/**
 * How far below the stored row height an overflowing table's `hugged` report may
 * pull the row. The hug exists only to trim the sub-row snap remainder (< 1 table
 * row, plus a scrollbar lane) so it must never exceed roughly one row. A hug
 * further below stored means the report came from a transiently-collapsed
 * container — e.g. an early sizing pass before AG Grid's row model was ready
 * reported the tiny empty-grid reserve as `natural`, the row auto-collapsed to
 * it, and the grid then clamped to the collapsed row. Honoring such a hug locks
 * the row at the transient height forever (each pass re-derives `hugged` from
 * the already-collapsed container); falling back to the stored height instead
 * lets the next sizing pass re-clamp at the intended height and re-report a
 * sane hug.
 */
export const TABLE_HUG_MAX_TRIM_PX = 80;

// Default row heights based on content type
export const ROW_HEIGHT_SECTION_HEADER = 80; // For section header rows
const ROW_HEIGHT_SMALL = 160; // For metric-only rows
const ROW_HEIGHT_TEXT_BLOCK = 200; // For text block rows
const ROW_HEIGHT_MEDIUM = 320; // For charts and insights
const ROW_HEIGHT_LARGE = 500; // For tables
const ROW_HEIGHT_AI_IMAGE = 560; // For AI-generated infographic images (1376x768 aspect ratio)

// --------------------------------
// Layout Utility Functions
// --------------------------------

/**
 * Generate a unique ID for a new column.
 */
export function generateColumnId(): string {
  return `col-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Column id for a column derived from (not created by) a block — the recompute paths that
 * re-run on every `blocks` identity change: computeLayoutFromBlocks, mergeLayoutWithBlocks's
 * new-block columns, and enforceSectionHeaderIsolation.
 *
 * These MUST be deterministic. Column ids key the ColumnStack fragments, so a fresh
 * `generateColumnId()` on every recompute remounts the whole subtree — which unmounts an open
 * edit popover mid-edit, and with instant-apply controls that means the panel closes on the
 * first click. A block lives in exactly one column and every one of these sites builds a
 * single-block column, so keying off the block id is unique; it also can't collide with a
 * saved `col-<timestamp>-<rand>` id.
 */
export function columnIdForBlock(blockId: string): string {
  return `col-for-${blockId}`;
}

/**
 * Generate a unique ID for a new row.
 */
export function generateRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/** Block types for each block in row column order (omits unknown / missing blocks). */
export function getRowBlockTypes(row: BlockRow, blockMap: Map<string, Block>): string[] {
  return row.columns
    .flatMap(col => col.blockIds)
    .flatMap(id => {
      const t = blockMap.get(id)?.type;
      return t != null ? [t as string] : [];
    });
}

/**
 * True when every block in the row is a metric_card. These rows size to their content: the
 * stored height is a minimum, not a fixed height, so a KPI with a long label or status grows
 * the whole row instead of clipping, and `items-stretch` keeps every card in the row the same
 * height. Mixed rows keep a fixed height because charts and tables need a definite height.
 */
export function isMetricOnlyRow(row: BlockRow, blockMap: Map<string, Block>): boolean {
  const blockTypes = getRowBlockTypes(row, blockMap);
  return blockTypes.length > 0 && blockTypes.every(type => type === "metric_card");
}

/**
 * Visual row height with data-table auto-collapse: a row whose only content is a
 * single data table renders at the table's measured natural content height when
 * that's SHORTER than the stored height — a 2-row table shouldn't sit in a card
 * of blank space. The stored `row.height` is intent, not a hard rule: it stays
 * untouched as the cap, so the table grows back up to it (then scrolls) when a
 * refresh brings more rows, and the collapse follows live quick-filtering.
 * Rows with anything other than exactly one data_table block keep the stored
 * height — a sibling block's height can't be dictated by the table's data.
 *
 * One exception on the cap: content that misses fitting by no more than
 * TABLE_GROW_TO_FIT_TOLERANCE_PX grows the row to fit — a table cut off a
 * near-miss short of whole (its last row sliced at the fold) reads as a
 * rendering bug, while ~a row of extra height is invisible. A genuinely tall
 * table still respects the cap and scrolls.
 *
 * When content overflows beyond that tolerance, the row hugs the grid as
 * actually rendered (`hugged`: clamped + snapped to a whole row) rather than
 * holding the stored height — the snap remainder (< 1 row) would otherwise sit
 * as a dead band above the card's bottom padding.
 */
export function computeEffectiveRowHeight(
  row: BlockRow,
  blockMap: Map<string, Block>,
  tableContentHeights: Record<string, TableContentHeightReport> | undefined,
  fallbackHeight: number
): number {
  const storedHeight = row.height ?? fallbackHeight;
  if (!tableContentHeights) {
    return storedHeight;
  }
  const blockIds = row.columns.flatMap(col => col.blockIds);
  if (blockIds.length !== 1) {
    return storedHeight;
  }
  const blockId = blockIds[0];
  if (blockMap.get(blockId)?.type !== "data_table") {
    return storedHeight;
  }
  const report = tableContentHeights[blockId];
  const natural = report?.natural;
  if (typeof natural !== "number" || !Number.isFinite(natural) || natural <= 0) {
    return storedHeight;
  }
  const naturalHeight = Math.max(Math.ceil(natural), MIN_ROW_HEIGHT);
  if (
    naturalHeight > storedHeight &&
    naturalHeight - storedHeight <= TABLE_GROW_TO_FIT_TOLERANCE_PX
  ) {
    return naturalHeight;
  }
  const hugged =
    typeof report.hugged === "number" && Number.isFinite(report.hugged) && report.hugged > 0
      ? report.hugged
      : natural;
  // Overflowing content: hug the rendered grid, but only within the snap
  // remainder — a hug deeper than TABLE_HUG_MAX_TRIM_PX below stored reflects a
  // transiently-collapsed container and would lock the row there (see the
  // constant's doc). Fall back to the stored height so sizing re-clamps at it.
  const huggedHeight =
    storedHeight - Math.ceil(hugged) <= TABLE_HUG_MAX_TRIM_PX ? Math.ceil(hugged) : storedHeight;
  const targetHeight = naturalHeight > storedHeight ? huggedHeight : naturalHeight;
  return Math.min(storedHeight, Math.max(targetHeight, MIN_ROW_HEIGHT));
}

/**
 * Determine the default row height based on block types in the row.
 * Priority: table (large) > ai_image (ai_image) > chart/insights (medium) > metrics (small)
 */
export function getDefaultRowHeight(blockTypes: string[]): number {
  const hasTable = blockTypes.includes("data_table");
  const hasAIImage = blockTypes.includes("ai_image");
  const hasChartOrInsights = blockTypes.includes("chart") || blockTypes.includes("ai_insights");
  const hasOnlyMetrics = blockTypes.every(type => type === "metric_card");
  const hasOnlySectionHeaders = blockTypes.every(type => type === "section_header");
  const hasOnlyTextBlocks = blockTypes.every(type => type === "text_block");

  if (hasTable) {
    return ROW_HEIGHT_LARGE;
  }
  if (hasAIImage) {
    return ROW_HEIGHT_AI_IMAGE;
  }
  if (hasChartOrInsights) {
    return ROW_HEIGHT_MEDIUM;
  }
  if (hasOnlyMetrics) {
    return ROW_HEIGHT_SMALL;
  }
  if (hasOnlySectionHeaders) {
    return ROW_HEIGHT_SECTION_HEADER;
  }
  if (hasOnlyTextBlocks) {
    return ROW_HEIGHT_TEXT_BLOCK;
  }
  return ROW_HEIGHT_MEDIUM;
}

/**
 * Compute the initial layout from a flat array of blocks.
 * - Metric cards are grouped together in the first row(s)
 * - Other blocks are grouped into rows based on their grid_cols values (max 4 cols per row)
 * - Each block starts in its own column (no vertical stacking by default)
 * - Row heights are set based on content type: small for metrics, medium for charts/insights, large for tables
 */
export function computeLayoutFromBlocks(blocks: Block[]): DashboardLayout {
  const metricCards = blocks.filter(block => block.type === "metric_card");
  const otherBlocks = blocks.filter(block => block.type !== "metric_card");

  const rows: BlockRow[] = [];

  // Add metric cards to rows (max 12 columns per row)
  if (metricCards.length > 0) {
    for (let i = 0; i < metricCards.length; i += MAX_COLUMNS_PER_ROW) {
      const rowMetricCards = metricCards.slice(i, i + MAX_COLUMNS_PER_ROW);
      rows.push({
        rowId: `row-${rows.length}`,
        columns: rowMetricCards.map(card => ({
          id: columnIdForBlock(card.block_id),
          blockIds: [card.block_id],
        })),
        height: ROW_HEIGHT_SMALL,
      });
    }
  }

  // Add other blocks in subsequent rows
  let currentRow: BlockRow = { rowId: `row-${rows.length}`, columns: [] };
  let currentRowCols = 0;
  let currentRowBlockTypes: string[] = [];

  const finalizeRow = () => {
    if (currentRow.columns.length > 0) {
      currentRow.height = getDefaultRowHeight(currentRowBlockTypes);
      rows.push(currentRow);
    }
  };

  otherBlocks.forEach(block => {
    // Section headers always get their own full-width row
    if (block.type === "section_header") {
      finalizeRow();
      currentRow = { rowId: `row-${rows.length}`, columns: [] };
      currentRowCols = 0;
      currentRowBlockTypes = [];

      currentRow.columns.push({
        id: columnIdForBlock(block.block_id),
        blockIds: [block.block_id],
      });
      currentRowBlockTypes.push(block.type);
      finalizeRow();
      currentRow = { rowId: `row-${rows.length}`, columns: [] };
      currentRowCols = 0;
      currentRowBlockTypes = [];
      return;
    }

    const blockCols = block.grid_cols ?? 4;

    // If adding this block would exceed 4 cols, start a new row
    if (currentRowCols + blockCols > 4 && currentRow.columns.length > 0) {
      finalizeRow();
      currentRow = { rowId: `row-${rows.length}`, columns: [] };
      currentRowCols = 0;
      currentRowBlockTypes = [];
    }

    // Each block starts in its own column
    currentRow.columns.push({
      id: columnIdForBlock(block.block_id),
      blockIds: [block.block_id],
    });
    currentRowCols += blockCols;
    currentRowBlockTypes.push(block.type);

    // If row is exactly full, finalize it
    if (currentRowCols >= 4) {
      finalizeRow();
      currentRow = { rowId: `row-${rows.length}`, columns: [] };
      currentRowCols = 0;
      currentRowBlockTypes = [];
    }
  });

  // Don't forget the last partial row
  finalizeRow();

  return { rows };
}

/**
 * Find the location of a block in the layout.
 * Returns { rowIndex, columnIndex, blockIndex } or null if not found.
 */
export function findBlockLocation(
  layout: DashboardLayout,
  blockId: string
): { rowIndex: number; columnIndex: number; blockIndex: number } | null {
  for (let rowIndex = 0; rowIndex < layout.rows.length; rowIndex++) {
    const row = layout.rows[rowIndex];
    for (let columnIndex = 0; columnIndex < row.columns.length; columnIndex++) {
      const column = row.columns[columnIndex];
      const blockIndex = column.blockIds.indexOf(blockId);
      if (blockIndex !== -1) {
        return { rowIndex, columnIndex, blockIndex };
      }
    }
  }
  return null;
}

/**
 * Get all block IDs in a row (flattened from all columns).
 */
function getRowBlockIds(row: BlockRow): string[] {
  return row.columns.flatMap(col => col.blockIds);
}

/**
 * Move a block within the layout. Supports:
 * - Moving between columns (horizontal reorder)
 * - Moving within a column's vertical stack
 * - Adding to an existing column's stack (via overColumnId)
 */
export function moveBlockInLayout(
  layout: DashboardLayout,
  activeBlockId: string,
  overBlockId: string | null,
  overRowId: string | null,
  blockMap?: Map<string, Block>,
  overColumnId?: string | null,
  insertBefore?: boolean
): DashboardLayout {
  const newRows = layout.rows.map(row => ({
    ...row,
    columns: row.columns.map(col => ({
      ...col,
      blockIds: [...col.blockIds],
    })),
    flexWidths: row.flexWidths ? [...row.flexWidths] : undefined,
  }));

  // Find source location
  const sourceLocation = findBlockLocation({ rows: newRows }, activeBlockId);
  if (!sourceLocation) {
    return layout;
  }

  const {
    rowIndex: sourceRowIndex,
    columnIndex: sourceColumnIndex,
    blockIndex: sourceBlockIndex,
  } = sourceLocation;
  const sourceRow = newRows[sourceRowIndex];
  const sourceColumn = sourceRow.columns[sourceColumnIndex];

  // Store the flex width of the column being potentially removed
  let sourceColumnFlexWidth: number | undefined;
  if (sourceRow.flexWidths && sourceRow.flexWidths.length > sourceColumnIndex) {
    sourceColumnFlexWidth = sourceRow.flexWidths[sourceColumnIndex];
  }

  // Remove from source column
  sourceColumn.blockIds.splice(sourceBlockIndex, 1);

  // If the source column is now empty, remove it (auto-collapse)
  const sourceColumnBecameEmpty = sourceColumn.blockIds.length === 0;
  if (sourceColumnBecameEmpty) {
    sourceRow.columns.splice(sourceColumnIndex, 1);
    if (sourceRow.flexWidths) {
      sourceRow.flexWidths.splice(sourceColumnIndex, 1);
    }
  }

  // Determine target row
  let targetRowIndex = -1;
  if (overRowId) {
    targetRowIndex = newRows.findIndex(row => row.rowId === overRowId);
  }
  if (targetRowIndex === -1 && overBlockId) {
    // Find row containing the over block
    for (let i = 0; i < newRows.length; i++) {
      if (getRowBlockIds(newRows[i]).includes(overBlockId)) {
        targetRowIndex = i;
        break;
      }
    }
  }
  if (targetRowIndex === -1 && overColumnId) {
    // Find row containing the over column
    for (let i = 0; i < newRows.length; i++) {
      if (newRows[i].columns.some(col => col.id === overColumnId)) {
        targetRowIndex = i;
        break;
      }
    }
  }
  if (targetRowIndex === -1) {
    // Fall back to source row
    targetRowIndex = sourceRowIndex;
  }

  const targetRow = newRows[targetRowIndex];

  // Case 1: Adding to an existing column's vertical stack
  if (overColumnId) {
    const targetColumnIndex = targetRow.columns.findIndex(col => col.id === overColumnId);
    if (targetColumnIndex !== -1) {
      const targetColumn = targetRow.columns[targetColumnIndex];
      // If overBlockId is provided and is in this column, insert relative to it
      if (overBlockId) {
        const overBlockIndex = targetColumn.blockIds.indexOf(overBlockId);
        if (overBlockIndex !== -1) {
          targetColumn.blockIds.splice(overBlockIndex + 1, 0, activeBlockId);
        } else {
          targetColumn.blockIds.push(activeBlockId);
        }
      } else {
        // Add to end of stack
        targetColumn.blockIds.push(activeBlockId);
      }

      // Remove empty rows and re-index
      return cleanupLayout(newRows);
    }
  }

  // Case 2: Moving to a position relative to another block (creates new column or reorders)
  if (overBlockId) {
    // Find which column contains the over block
    let targetColumnIndex = -1;
    let overBlockIndexInColumn = -1;
    for (let i = 0; i < targetRow.columns.length; i++) {
      const idx = targetRow.columns[i].blockIds.indexOf(overBlockId);
      if (idx !== -1) {
        targetColumnIndex = i;
        overBlockIndexInColumn = idx;
        break;
      }
    }

    if (targetColumnIndex !== -1) {
      // Check if we can add a new column (max 12 columns per row)
      if (targetRow.columns.length >= MAX_COLUMNS_PER_ROW) {
        // Can't add more columns, return layout unchanged
        return layout;
      }

      // Insert as a new column next to the target column
      const newColumn: LayoutColumn = {
        id: generateColumnId(),
        blockIds: [activeBlockId],
      };

      // Determine insert position - before or after target column based on insertBefore flag
      const insertIndex = insertBefore ? targetColumnIndex : targetColumnIndex + 1;
      targetRow.columns.splice(insertIndex, 0, newColumn);

      // Handle flexWidths
      if (targetRow.flexWidths) {
        const widthToInsert = sourceColumnFlexWidth ?? blockMap?.get(activeBlockId)?.grid_cols ?? 4;
        targetRow.flexWidths.splice(insertIndex, 0, widthToInsert);
      }

      return cleanupLayout(newRows);
    }
  }

  // Case 3: Adding as a new column at the end of the row
  // Check if we can add a new column (max 12 columns per row)
  if (targetRow.columns.length >= MAX_COLUMNS_PER_ROW) {
    // Can't add more columns, return layout unchanged
    return layout;
  }

  const newColumn: LayoutColumn = {
    id: generateColumnId(),
    blockIds: [activeBlockId],
  };
  targetRow.columns.push(newColumn);

  // Handle flexWidths
  if (targetRow.flexWidths) {
    const widthToInsert = sourceColumnFlexWidth ?? blockMap?.get(activeBlockId)?.grid_cols ?? 4;
    targetRow.flexWidths.push(widthToInsert);
  }

  return cleanupLayout(newRows);
}

/**
 * Ensure section_header blocks are always alone in their own full-width row.
 * If a section header ended up sharing a row with other blocks (e.g. via drag-and-drop),
 * it is split out into its own row inserted at the same position.
 */
export function enforceSectionHeaderIsolation(
  layout: DashboardLayout,
  blockMap: Map<string, Block>
): DashboardLayout {
  const newRows: BlockRow[] = [];

  for (const row of layout.rows) {
    const allBlockIds = row.columns.flatMap(col => col.blockIds);
    const sectionHeaderIds = allBlockIds.filter(id => blockMap.get(id)?.type === "section_header");

    if (sectionHeaderIds.length === 0) {
      newRows.push(row);
      continue;
    }

    // Section header already alone — just ensure correct height
    if (sectionHeaderIds.length === allBlockIds.length && row.columns.length === 1) {
      newRows.push({ ...row, height: row.height ?? ROW_HEIGHT_SECTION_HEADER });
      continue;
    }

    // Split: extract section headers into their own rows, keep the rest together
    const nonSectionColumns: LayoutColumn[] = [];
    for (const col of row.columns) {
      const sectionIds = col.blockIds.filter(id => blockMap.get(id)?.type === "section_header");
      const otherIds = col.blockIds.filter(id => blockMap.get(id)?.type !== "section_header");

      for (const shId of sectionIds) {
        newRows.push({
          rowId: generateRowId(),
          columns: [{ id: columnIdForBlock(shId), blockIds: [shId] }],
          height: ROW_HEIGHT_SECTION_HEADER,
        });
      }

      if (otherIds.length > 0) {
        nonSectionColumns.push({ ...col, blockIds: otherIds });
      }
    }

    if (nonSectionColumns.length > 0) {
      newRows.push({
        ...row,
        columns: nonSectionColumns,
        flexWidths: undefined,
      });
    }
  }

  newRows.forEach((r, i) => {
    r.rowId = `row-${i}`;
  });
  return { rows: newRows };
}

/**
 * Helper to clean up layout: remove empty rows and re-index row IDs.
 */
function cleanupLayout(rows: BlockRow[]): DashboardLayout {
  // Remove empty rows (rows with no columns)
  const filteredRows = rows.filter(row => row.columns.length > 0);

  // Re-index row IDs
  filteredRows.forEach((row, index) => {
    row.rowId = `row-${index}`;
  });

  return { rows: filteredRows };
}

/**
 * Add a block to an existing column's vertical stack.
 * @param position - "top" adds at the beginning, "bottom" adds at the end
 */
export function addBlockToColumnStack(
  layout: DashboardLayout,
  blockId: string,
  targetColumnId: string,
  position: "top" | "bottom" = "bottom"
): DashboardLayout {
  const newRows = layout.rows.map(row => ({
    ...row,
    columns: row.columns.map(col => ({
      ...col,
      blockIds: [...col.blockIds],
    })),
    flexWidths: row.flexWidths ? [...row.flexWidths] : undefined,
  }));

  // First, remove the block from its current location
  for (const row of newRows) {
    for (let colIndex = 0; colIndex < row.columns.length; colIndex++) {
      const column = row.columns[colIndex];
      const blockIndex = column.blockIds.indexOf(blockId);
      if (blockIndex !== -1) {
        column.blockIds.splice(blockIndex, 1);
        // If column is now empty, remove it
        if (column.blockIds.length === 0) {
          row.columns.splice(colIndex, 1);
          if (row.flexWidths && row.flexWidths.length > colIndex) {
            row.flexWidths.splice(colIndex, 1);
          }
        }
        break;
      }
    }
  }

  // Now add to the target column
  for (const row of newRows) {
    const targetColumn = row.columns.find(col => col.id === targetColumnId);
    if (targetColumn) {
      if (position === "top") {
        targetColumn.blockIds.unshift(blockId);
      } else {
        targetColumn.blockIds.push(blockId);
      }
      break;
    }
  }

  return cleanupLayout(newRows);
}

/**
 * Add a block directly above another block in the same column.
 */
export function addBlockAboveInColumn(
  layout: DashboardLayout,
  blockIdToMove: string,
  targetBlockId: string
): DashboardLayout {
  const newRows = layout.rows.map(row => ({
    ...row,
    columns: row.columns.map(col => ({
      ...col,
      blockIds: [...col.blockIds],
    })),
    flexWidths: row.flexWidths ? [...row.flexWidths] : undefined,
  }));

  // Find which column the target block is in
  let targetColumn: { id: string; blockIds: string[] } | null = null;

  for (const row of newRows) {
    for (const column of row.columns) {
      const idx = column.blockIds.indexOf(targetBlockId);
      if (idx !== -1) {
        targetColumn = column;
        break;
      }
    }
    if (targetColumn) {
      break;
    }
  }

  if (!targetColumn) {
    // Target block not found, return unchanged
    return layout;
  }

  // First, remove the block from its current location (if it exists elsewhere)
  for (const row of newRows) {
    for (let colIndex = 0; colIndex < row.columns.length; colIndex++) {
      const column = row.columns[colIndex];
      const blockIndex = column.blockIds.indexOf(blockIdToMove);
      if (blockIndex !== -1) {
        column.blockIds.splice(blockIndex, 1);
        // If column is now empty, remove it
        if (column.blockIds.length === 0) {
          row.columns.splice(colIndex, 1);
          if (row.flexWidths && row.flexWidths.length > colIndex) {
            row.flexWidths.splice(colIndex, 1);
          }
        }
        break;
      }
    }
  }

  // Now insert the block right before the target block in its column
  // We need to re-find the target block since indices may have changed
  for (const row of newRows) {
    for (const column of row.columns) {
      const idx = column.blockIds.indexOf(targetBlockId);
      if (idx !== -1) {
        // Insert before the target block
        column.blockIds.splice(idx, 0, blockIdToMove);
        break;
      }
    }
  }

  return cleanupLayout(newRows);
}

/**
 * Add a block directly below another block in the same column.
 * If the target block is not in a column, this creates a new column with both blocks.
 */
export function addBlockBelowInColumn(
  layout: DashboardLayout,
  blockIdToMove: string,
  targetBlockId: string
): DashboardLayout {
  const newRows = layout.rows.map(row => ({
    ...row,
    columns: row.columns.map(col => ({
      ...col,
      blockIds: [...col.blockIds],
    })),
    flexWidths: row.flexWidths ? [...row.flexWidths] : undefined,
  }));

  // Find which column the target block is in
  let targetColumn: { id: string; blockIds: string[] } | null = null;
  let targetBlockIndex = -1;

  for (const row of newRows) {
    for (const column of row.columns) {
      const idx = column.blockIds.indexOf(targetBlockId);
      if (idx !== -1) {
        targetColumn = column;
        targetBlockIndex = idx;
        break;
      }
    }
    if (targetColumn) {
      break;
    }
  }

  if (!targetColumn) {
    // Target block not found, return unchanged
    return layout;
  }

  // First, remove the block from its current location (if it exists elsewhere)
  for (const row of newRows) {
    for (let colIndex = 0; colIndex < row.columns.length; colIndex++) {
      const column = row.columns[colIndex];
      const blockIndex = column.blockIds.indexOf(blockIdToMove);
      if (blockIndex !== -1) {
        column.blockIds.splice(blockIndex, 1);
        // If column is now empty, remove it
        if (column.blockIds.length === 0) {
          row.columns.splice(colIndex, 1);
          if (row.flexWidths && row.flexWidths.length > colIndex) {
            row.flexWidths.splice(colIndex, 1);
          }
        }
        break;
      }
    }
  }

  // Now insert the block right after the target block in its column
  // We need to re-find the target block since indices may have changed
  for (const row of newRows) {
    for (const column of row.columns) {
      const idx = column.blockIds.indexOf(targetBlockId);
      if (idx !== -1) {
        // Insert after the target block
        column.blockIds.splice(idx + 1, 0, blockIdToMove);
        break;
      }
    }
  }

  return cleanupLayout(newRows);
}

/**
 * Move a block within a column's vertical stack (reorder within the stack).
 */
export function reorderBlockInColumn(
  layout: DashboardLayout,
  blockId: string,
  targetColumnId: string,
  targetIndex: number
): DashboardLayout {
  const newRows = layout.rows.map(row => ({
    ...row,
    columns: row.columns.map(col => ({
      ...col,
      blockIds: [...col.blockIds],
    })),
    flexWidths: row.flexWidths ? [...row.flexWidths] : undefined,
  }));

  // Find the target column and reorder
  for (const row of newRows) {
    const targetColumn = row.columns.find(col => col.id === targetColumnId);
    if (targetColumn) {
      const currentIndex = targetColumn.blockIds.indexOf(blockId);
      if (currentIndex !== -1) {
        // Remove from current position
        targetColumn.blockIds.splice(currentIndex, 1);
        // Insert at new position (adjust if moving down since array shrunk)
        const insertIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
        targetColumn.blockIds.splice(insertIndex, 0, blockId);
      }
      break;
    }
  }

  return { rows: newRows };
}

/**
 * Get a flattened, ordered array of block IDs from the layout.
 * Order: row by row, column by column, then blocks within each column.
 */
export function getOrderedBlockIds(layout: DashboardLayout): string[] {
  return layout.rows.flatMap(row => row.columns.flatMap(col => col.blockIds));
}

/**
 * Reorder `blocks` to follow the layout's reading order.
 *
 * Blocks the layout does not mention keep their existing relative order and are appended
 * rather than dropped: a layout only describes arrangement, so one that omits a block must
 * never delete it. Layouts are routinely partial — `layout` is optional on the content spec,
 * and a saved one can predate blocks added since — so filtering to the placed set would
 * silently discard real content. The renderer folds the appended blocks back into rows via
 * {@link mergeLayoutWithBlocks}, so an omission costs position at worst.
 */
export function reorderBlocksToLayout(blocks: Block[], layout: DashboardLayout): Block[] {
  const blockMap = new Map(blocks.map(b => [b.block_id, b]));
  const orderedIds = getOrderedBlockIds(layout);
  const placedIds = new Set(orderedIds);
  const placed = orderedIds.map(id => blockMap.get(id)).filter((b): b is Block => b !== undefined);
  return [...placed, ...blocks.filter(b => !placedIds.has(b.block_id))];
}

/**
 * The layout a dashboard is actually rendered with.
 *
 * `layout` is optional on the content spec and can also be stale, so callers that need to
 * position something relative to what the user sees must resolve it the way
 * DraggableBlocksSection does before rendering — not read `spec.layout` raw.
 */
export function resolveEffectiveLayout(
  layout: DashboardLayout | undefined,
  blocks: Block[]
): DashboardLayout {
  return layout && layout.rows.length > 0
    ? mergeLayoutWithBlocks(layout, blocks)
    : computeLayoutFromBlocks(blocks);
}

/**
 * Insert a block into a new row at a specific position in the layout.
 */
export function insertBlockAsNewRow(
  layout: DashboardLayout,
  blockId: string,
  insertAtRowIndex: number
): DashboardLayout {
  const newRows: BlockRow[] = layout.rows.map(row => ({
    ...row,
    columns: row.columns.map(col => ({
      ...col,
      blockIds: [...col.blockIds],
    })),
    flexWidths: row.flexWidths ? [...row.flexWidths] : undefined,
  }));

  // Find and remove the block from its current position
  for (let i = 0; i < newRows.length; i++) {
    const row = newRows[i];
    for (let j = 0; j < row.columns.length; j++) {
      const column = row.columns[j];
      const blockIndex = column.blockIds.indexOf(blockId);
      if (blockIndex !== -1) {
        column.blockIds.splice(blockIndex, 1);
        // If column is now empty, remove it
        if (column.blockIds.length === 0) {
          row.columns.splice(j, 1);
          if (row.flexWidths && row.flexWidths.length > j) {
            row.flexWidths.splice(j, 1);
          }
        }
        break;
      }
    }
  }

  // Create a new row with the block in a single column
  const newRow: BlockRow = {
    rowId: generateRowId(),
    columns: [
      {
        id: generateColumnId(),
        blockIds: [blockId],
      },
    ],
  };

  // Insert the new row at the specified position
  newRows.splice(insertAtRowIndex, 0, newRow);

  // Remove empty rows
  const filteredRows = newRows.filter(row => row.columns.length > 0);

  // Re-index row IDs
  filteredRows.forEach((row, index) => {
    row.rowId = `row-${index}`;
  });

  return { rows: filteredRows };
}

/**
 * Remove a block from the layout.
 */
export function removeBlockFromLayout(layout: DashboardLayout, blockId: string): DashboardLayout {
  const newRows = layout.rows.map(row => {
    // Check each column for the block
    let columnIndexToRemove = -1;
    const newColumns = row.columns.map((column, columnIndex) => {
      const blockIndex = column.blockIds.indexOf(blockId);
      if (blockIndex === -1) {
        return column;
      }

      const newBlockIds = column.blockIds.filter(id => id !== blockId);

      // Mark column for removal if it became empty
      if (newBlockIds.length === 0) {
        columnIndexToRemove = columnIndex;
      }

      return {
        ...column,
        blockIds: newBlockIds,
      };
    });

    // Remove empty column and its flex width
    const filteredColumns = newColumns.filter(col => col.blockIds.length > 0);
    let newFlexWidths = row.flexWidths;
    if (
      columnIndexToRemove !== -1 &&
      row.flexWidths &&
      row.flexWidths.length > columnIndexToRemove
    ) {
      newFlexWidths = [...row.flexWidths];
      newFlexWidths.splice(columnIndexToRemove, 1);
    }

    return {
      ...row,
      columns: filteredColumns,
      flexWidths: newFlexWidths,
    };
  });

  // Remove empty rows
  const filteredRows = newRows.filter(row => row.columns.length > 0);

  // Re-index row IDs
  filteredRows.forEach((row, index) => {
    row.rowId = `row-${index}`;
  });

  return { rows: filteredRows };
}

/**
 * Update the flex widths for blocks in a specific row.
 */
export function updateRowFlexWidths(
  layout: DashboardLayout,
  rowIndex: number,
  newFlexWidths: number[]
): DashboardLayout {
  return {
    ...layout,
    rows: layout.rows.map((row, idx) =>
      idx === rowIndex ? { ...row, flexWidths: newFlexWidths } : row
    ),
  };
}

/**
 * Get the flex widths for a row's columns, falling back to grid_cols from first block in each column.
 */
export function getRowFlexWidths(row: BlockRow, blockMap: Map<string, Block>): number[] {
  if (row.flexWidths && row.flexWidths.length === row.columns.length) {
    return row.flexWidths;
  }
  // Fallback: derive from grid_cols of first block in each column
  return row.columns.map(column => {
    const firstBlockId = column.blockIds[0];
    if (firstBlockId) {
      const block = blockMap.get(firstBlockId);
      return block?.grid_cols ?? 4;
    }
    return 4;
  });
}

/**
 * Snap flex widths to a 12-column grid.
 * Scales the widths so total equals 12, rounds to nearest integer,
 * and ensures each block has at least 2 columns.
 *
 * Exception: when every input width is equal, the columns stay exactly equal
 * by returning the fractional share 12 / count (e.g. 5 columns -> 2.4 each).
 * The render formula in DraggableBlocksSection
 * (`calc(span * (100% + gap) / 12 - gap)`) handles fractional spans, so the row
 * stays full-width with perfectly equal columns. Rounding equal inputs to
 * integers + redistributing to sum 12 would instead turn them UNEQUAL
 * (e.g. [2.4 x5] -> [3, 3, 2, 2, 2]), which is the bug this guards against.
 */
export function snapFlexWidthsTo12ColumnGrid(flexWidths: number[]): number[] {
  if (flexWidths.length === 0) {
    return [];
  }

  if (flexWidths.length === 1) {
    return [12];
  }

  const totalWidth = flexWidths.reduce((sum, w) => sum + w, 0);
  const minColumns = 2;
  const totalColumns = 12;

  // Equal inputs must render equal. 12 is not divisible by every column count
  // (e.g. 5), so the integer rounding + fill-to-12 redistribution below would
  // make an equal row visibly unequal. Return the exact fractional share so
  // the row is both full and equal.
  const allEqual = flexWidths.every(w => Math.abs(w - flexWidths[0]) < 1e-6);
  if (allEqual) {
    const equalWidth = Math.max(minColumns, totalColumns / flexWidths.length);
    return flexWidths.map(() => equalWidth);
  }

  // Scale to 12 columns and round
  const snappedWidths = flexWidths.map(w => {
    const scaled = (w / totalWidth) * totalColumns;
    return Math.max(minColumns, Math.round(scaled));
  });

  // Adjust for rounding errors to ensure total is exactly 12
  let snappedTotal = snappedWidths.reduce((sum, w) => sum + w, 0);

  while (snappedTotal !== totalColumns) {
    const diff = totalColumns - snappedTotal;

    if (diff > 0) {
      // Need to add columns - find the block with the largest fractional loss
      let maxLossIndex = 0;
      let maxLoss = -Infinity;
      flexWidths.forEach((w, i) => {
        const ideal = (w / totalWidth) * totalColumns;
        const loss = ideal - snappedWidths[i];
        if (loss > maxLoss && snappedWidths[i] < totalColumns - (flexWidths.length - 1)) {
          maxLoss = loss;
          maxLossIndex = i;
        }
      });
      snappedWidths[maxLossIndex]++;
    } else {
      // Need to remove columns - find the block with the largest fractional gain
      let maxGainIndex = 0;
      let maxGain = -Infinity;
      flexWidths.forEach((w, i) => {
        const ideal = (w / totalWidth) * totalColumns;
        const gain = snappedWidths[i] - ideal;
        if (gain > maxGain && snappedWidths[i] > minColumns) {
          maxGain = gain;
          maxGainIndex = i;
        }
      });
      snappedWidths[maxGainIndex]--;
    }

    snappedTotal = snappedWidths.reduce((sum, w) => sum + w, 0);
  }

  return snappedWidths;
}

/**
 * Maximum number of columns allowed per row when enforcing minimum width of 2.
 * With a 12-column grid and minimum width 2, we can have at most 6 columns per row.
 */
const MAX_COLUMNS_WITH_MIN_WIDTH_2 = 6;

/**
 * Split a row if it has too many columns to fit with minimum width 2.
 * Moves rightmost columns to a new row with the same height.
 */
function splitRowIfOverflow(row: BlockRow): BlockRow[] {
  if (row.columns.length <= MAX_COLUMNS_WITH_MIN_WIDTH_2) {
    return [row]; // No overflow
  }

  // Split: keep first 6 columns, move rest to new row
  const firstRowColumns = row.columns.slice(0, MAX_COLUMNS_WITH_MIN_WIDTH_2);
  const overflowColumns = row.columns.slice(MAX_COLUMNS_WITH_MIN_WIDTH_2);

  // Recursively split if overflow row still has too many columns
  // Preserve height for both rows
  const firstRow: BlockRow = { ...row, columns: firstRowColumns, flexWidths: undefined };
  const overflowRow: BlockRow = {
    rowId: generateRowId(),
    columns: overflowColumns,
    height: row.height,
  };

  return [firstRow, ...splitRowIfOverflow(overflowRow)];
}

/**
 * Snap all rows' flex widths to the 12-column grid.
 * This ensures consistent alignment after drag-and-drop operations that
 * may add or remove columns from rows.
 * Also splits rows that have too many columns to fit with minimum width 2.
 */
export function snapLayoutFlexWidths(
  layout: DashboardLayout,
  blockMap: Map<string, Block>
): DashboardLayout {
  // First, split any rows that would overflow with min width 2
  const expandedRows: BlockRow[] = [];
  for (const row of layout.rows) {
    expandedRows.push(...splitRowIfOverflow(row));
  }

  // Then snap each row to 12-column grid and re-index row IDs
  return {
    ...layout,
    rows: expandedRows.map((row, index) => {
      // Get current flex widths (either from layout or derived from grid_cols)
      const currentFlexWidths = getRowFlexWidths(row, blockMap);

      // Snap to 12-column grid
      const snappedFlexWidths = snapFlexWidthsTo12ColumnGrid(currentFlexWidths);

      return {
        ...row,
        rowId: `row-${index}`,
        flexWidths: snappedFlexWidths,
      };
    }),
  };
}

/**
 * Merge a saved layout with the current set of blocks.
 * - Removes block IDs from the layout that no longer exist in blocks
 * - Adds new blocks (not in layout) near their neighbors in the blocks array order:
 *   tries to place the new block in the same row as its preceding neighbor (if space allows),
 *   otherwise inserts a new row immediately after the preceding neighbor's row.
 *   Falls back to preceding by next neighbor, then appends at the end.
 * - Preserves row heights from the saved layout
 */
export function mergeLayoutWithBlocks(
  savedLayout: DashboardLayout,
  blocks: Block[]
): DashboardLayout {
  // Get set of current block IDs (including metric cards)
  const currentBlockIds = new Set(blocks.map(block => block.block_id));

  // Get all block IDs that are in the saved layout
  const layoutBlockIds = new Set(getOrderedBlockIds(savedLayout));

  // Find blocks that are in currentBlockIds but not in layoutBlockIds (new blocks)
  const newBlockIds = Array.from(currentBlockIds).filter(id => !layoutBlockIds.has(id));

  // Filter out block IDs that no longer exist from each row
  const filteredRows: BlockRow[] = savedLayout.rows
    .map((row): BlockRow | null => {
      const newColumns = row.columns
        .map(column => ({
          ...column,
          blockIds: column.blockIds.filter(id => currentBlockIds.has(id)),
        }))
        .filter(column => column.blockIds.length > 0);

      if (newColumns.length === 0) {
        return null;
      }

      // Adjust flexWidths if columns were removed
      let newFlexWidths = row.flexWidths;
      if (row.flexWidths && newColumns.length !== row.columns.length) {
        // Rebuild flexWidths for remaining columns
        newFlexWidths = [];
        let originalIndex = 0;
        for (const column of row.columns) {
          if (column.blockIds.some(id => currentBlockIds.has(id))) {
            if (row.flexWidths[originalIndex] !== undefined) {
              newFlexWidths.push(row.flexWidths[originalIndex]);
            }
          }
          originalIndex++;
        }
      }

      return {
        ...row,
        columns: newColumns,
        flexWidths: newFlexWidths,
      };
    })
    .filter((row): row is BlockRow => row !== null);

  const blockIdSet = new Map(blocks.map(b => [b.block_id, b]));

  // Re-index row IDs and enforce minimum row height
  filteredRows.forEach((row, index) => {
    row.rowId = `row-${index}`;
    const rowBlockIds = row.columns.flatMap(col => col.blockIds);
    const isSectionHeaderOnly =
      rowBlockIds.length > 0 &&
      rowBlockIds.every(id => blockIdSet.get(id)?.type === "section_header");
    const minHeight = isSectionHeaderOnly ? ROW_HEIGHT_SECTION_HEADER : MIN_ROW_HEIGHT;
    if (row.height != null && row.height < minHeight) {
      row.height = minHeight;
    }
  });

  // Insert new blocks near their neighbors using the blocks array order.
  // Process new blocks in the order they appear in the blocks array so that
  // multiple new blocks inserted consecutively are placed in the right sequence.
  const newBlockIdSet = new Set(newBlockIds);
  const orderedNewBlockIds = blocks.map(b => b.block_id).filter(id => newBlockIdSet.has(id));

  for (const newBlockId of orderedNewBlockIds) {
    const blockIndex = blocks.findIndex(b => b.block_id === newBlockId);

    // Find the closest preceding block that already exists in the layout
    let precedingRowIndex = -1;
    for (let i = blockIndex - 1; i >= 0; i--) {
      const candidateId = blocks[i].block_id;
      const rowIdx = filteredRows.findIndex(r =>
        r.columns.some(c => c.blockIds.includes(candidateId))
      );
      if (rowIdx !== -1) {
        precedingRowIndex = rowIdx;
        break;
      }
    }

    // Find the closest following block that already exists in the layout
    let followingRowIndex = -1;
    for (let i = blockIndex + 1; i < blocks.length; i++) {
      const candidateId = blocks[i].block_id;
      const rowIdx = filteredRows.findIndex(r =>
        r.columns.some(c => c.blockIds.includes(candidateId))
      );
      if (rowIdx !== -1) {
        followingRowIndex = rowIdx;
        break;
      }
    }

    // Determine anchor row index and whether to insert before or after it
    let insertAfterRowIndex: number;
    if (precedingRowIndex !== -1) {
      insertAfterRowIndex = precedingRowIndex;
    } else if (followingRowIndex !== -1) {
      // No preceding neighbor — insert as a new row before the following neighbor's row
      insertAfterRowIndex = followingRowIndex - 1;
    } else {
      // No neighbors in layout at all — append at the end
      insertAfterRowIndex = filteredRows.length - 1;
    }

    const anchorRow = filteredRows[insertAfterRowIndex];

    // Add to the anchor row if it has room (fewer than MAX_COLUMNS_WITH_MIN_WIDTH_2 columns).
    // Section header isolation and flex-width snapping are enforced by the callers
    // (enforceSectionHeaderIsolation + snapLayoutFlexWidths) after this function returns.
    if (anchorRow && anchorRow.columns.length < MAX_COLUMNS_WITH_MIN_WIDTH_2) {
      const newCol = { id: columnIdForBlock(newBlockId), blockIds: [newBlockId] };
      // Find the column containing the preceding block so we insert right after it,
      // rather than appending to the end of the row.
      const precedingBlockId = blockIndex > 0 ? blocks[blockIndex - 1]?.block_id : undefined;
      const precedingColIndex =
        precedingBlockId != null
          ? anchorRow.columns.findIndex(c => c.blockIds.includes(precedingBlockId))
          : -1;
      if (precedingColIndex !== -1) {
        anchorRow.columns.splice(precedingColIndex + 1, 0, newCol);
      } else {
        anchorRow.columns.push(newCol);
      }
      // Clear flexWidths so snapLayoutFlexWidths recalculates for the new column count
      anchorRow.flexWidths = undefined;
    } else {
      // Insert as a new row immediately after the anchor row
      const newRow: BlockRow = {
        rowId: generateRowId(),
        columns: [{ id: columnIdForBlock(newBlockId), blockIds: [newBlockId] }],
      };
      filteredRows.splice(insertAfterRowIndex + 1, 0, newRow);
    }
  }

  // Re-index row IDs after all insertions
  filteredRows.forEach((row, index) => {
    row.rowId = `row-${index}`;
  });

  return { rows: filteredRows };
}
